import _ from "lodash";

import isValidDate from "date-fns/is_valid";
import subYears from "date-fns/sub_years";

import phoneNumber from "./phone-number";

import payment from "payment";

type ValidationParams = object;

export type ValidationResult = string | undefined;

export const required = (value?: string): ValidationResult =>
  value ? undefined : "Required";

export const nonEmptyString = (value?: string): ValidationResult =>
  _.isString(value) && _.trim(value) !== "" ? undefined : "Required";

export const email = (value?: string): ValidationResult =>
  value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)
    ? "Invalid email address"
    : undefined;

export const date = (value?: string): ValidationResult =>
  value && !isValidDate(new Date(value)) ? "Invalid date" : undefined;

export const dateOver21 = (value?: string): ValidationResult =>
  date(value) || new Date(value) > subYears(new Date(), 21) ? "Not over 21" : undefined;

export const maxLength = (max: number) => (value?: string): ValidationResult =>
  _.isString(value) && value.length > max
    ? `Must be ${max} characters or less`
    : undefined;

export const minLength = (min: number) => (value?: string): ValidationResult =>
  _.isString(value) && value.length < min
    ? `Must be ${min} characters or greater`
    : undefined;

export const range = (min: number, max: number) => (
  value?: string
): ValidationResult =>
  _.toNumber(value) >= min && _.toNumber(value) <= max
    ? undefined
    : `Falls outside of the accepted range of ${min} and ${max}`;

export const numeric = (value?: string): ValidationResult =>
  !_.isNaN(_.toNumber(value)) && _.isFinite(_.toNumber(value))
    ? undefined
    : "Invalid number";

export const phone = (value?: string): ValidationResult =>
  value && !phoneNumber.isValid(value) ? "Invalid phone number" : undefined;

export const cc = (value?: string): ValidationResult =>
  value && payment.fns.validateCardNumber(value)
    ? undefined
    : "invalid card number";

export const composeField = (
  ...validators: ((_?: string) => ValidationResult)[]
) => (value?: string): ValidationResult =>
  _.reduce(
    validators,
    (error: ValidationResult, validator: (_?: string) => ValidationResult) =>
      error || validator(value),
    undefined
  );

export const composeForm = (
  ...validators: ((
    values: ValidationParams,
    errors?: ValidationParams
  ) => ValidationResult)[]
) => (values: ValidationParams) =>
  _.reduce(
    validators,
    (errors, validator) => _.tap(errors, (errors) => validator(values, errors)),
    {}
  );

export const passwordConfirmation = (field: string, confirmField: string) => (
  values: ValidationParams,
  errors: ValidationParams
): void => {
  if (values[field] !== values[confirmField])
    errors[confirmField] = `Does not match ${field}`;
};

export const ccExpiry = (monthField: string, yearField: string) => (
  values: ValidationParams,
  errors: ValidationParams
): void => {
  if (!payment.fns.validateCardExpiry(values[monthField], values[yearField]))
    errors[yearField] = "Invalid expiration";
};

export const ccCvv = (cardNumberField: string, cvvField: string) => (
  values: ValidationParams,
  errors: ValidationParams
): void => {
  if (
    !payment.fns.validateCardCVC(
      values[cvvField],
      payment.fns.cardType(values[cardNumberField])
    )
  )
    errors[cvvField] = "Invalid CVV";
};

export const postalCode = (postalCode?: string): ValidationResult =>
  postalCode && !/^\d{5}(?:[-\s]\d{4})?$/i.test(postalCode)
    ? "Invalid Postal Code"
    : undefined;

export const ACCEPTED_IMAGE_TYPES = [
  "image/gif",
  "image/png",
  "image/jpeg",
  "image/jpg"
];

export const validImageType = (type?: string): ValidationResult =>
  _.includes(ACCEPTED_IMAGE_TYPES, type) ? undefined : "Invalid file type";
